import {
  DatabaseInstanceAgentV2Enum,
  DatabaseInstanceProxyV2Enum,
  DboCachingSummaryCacheModeEnum,
  DboDatabase,
  DboDatabaseInstanceV2,
  DboV2DatabaseInstancesResponse,
} from '@cast/types-database-optimizer';

import { DatabaseViewModelV2, InstanceViewModelV2 } from '../types';
import { buildDboRoute, DboRoute } from '../routes';
import { dboProtocolToLabel } from '../constants';
import { DEMO_ENTITY_IDS } from '../demo/demoInstances';
import isNil from 'lodash/isNil';
import toNumber from 'lodash/toNumber';

type DboV2InstancesOverviewModel = {
  optimized: InstanceViewModelV2[];
  other: InstanceViewModelV2[];
  noInstances: boolean;
  total: number;
};

const getCacheModeLabel = (mode?: DboCachingSummaryCacheModeEnum): string => {
  switch (mode) {
    case DboCachingSummaryCacheModeEnum.ON:
      return 'Auto';
    case DboCachingSummaryCacheModeEnum.PASSTHROUGH:
      return 'Passthrough';
    case DboCachingSummaryCacheModeEnum.OFF:
      return 'Cache off';
    case DboCachingSummaryCacheModeEnum.CACHE_MODE_UNSPECIFIED:
    default:
      return 'Unknown';
  }
};

const mapDatabaseToDashboardModel =
  (dbServerId: string, features: DboDatabaseInstanceV2['features']) =>
  ({
    name,
    advisorStats: advisor,
    cachingSummary: cache,
    connectionsSummary: conn,
    cacheConfigId,
    id,
  }: DboDatabase): DatabaseViewModelV2 => {
    const detailBase = { dbServerId, databaseId: id! };

    const openRecommendations =
      (advisor?.openQueryRecommendationsCount ?? 0) +
      (advisor?.openIndexRecommendationsCount ?? 0);

    const performanceAdvisor = {
      openRecommendations,
      hasRecommendations: openRecommendations > 0,
      analyzedTablesCount: advisor?.analyzedTablesCount ?? 0,
      analyzedIndexesCount: advisor?.analyzedIndexesCount ?? 0,
      affectedQueriesCount: advisor?.affectedQueriesCount ?? 0,
      affectedQueriesDbTimePercentage:
        advisor?.affectedQueriesDbTimePercentage ?? 0,
      affectedQueriesDbTimeMs: advisor?.affectedQueriesDbTimeMs ?? 0,
      detailUrl: buildDboRoute(DboRoute.DB_PERFORMANCE_ADVISOR, detailBase),
    };

    const cachingEnabled = cache?.cacheMode === 'ON';
    const cacheHits = cachingEnabled
      ? cache?.cacheHits
      : cache?.projectedCacheHits;

    const caching = {
      enabled: cachingEnabled,
      isOff: cache?.cacheMode === 'OFF',
      cacheMode: cache?.cacheMode,
      cacheModeLabel: getCacheModeLabel(cache?.cacheMode),
      cacheConfigId,
      totalQueries: cache?.totalQueries,
      dqlQueries: cache?.dqlQueries,
      cacheHits: isNil(cacheHits) ? 0 : toNumber(cacheHits),
      dbTimeSavePercentage:
        (cachingEnabled
          ? cache?.dbTimeSavePercentage
          : cache?.projectedDbTimeSavePercentage) ?? 0,
      hitRate: (cachingEnabled ? cache?.hitRate : cache?.projectedHitRate) ?? 0,
      totalTimeMs: cache?.totalTimeMs,
      detailUrl: buildDboRoute(DboRoute.DB_CACHING_OVERVIEW, detailBase),
    };

    const connections = {
      enabled: !!features?.pooling,
      reduction: conn?.reduction ?? 0,
      outgoing: conn?.outgoing ?? 0,
      incoming: conn?.incoming ?? 0,
      active: conn?.active ?? 0,
      idle: conn?.idle ?? 0,
      maxConnections: conn?.maxConnections ?? 0,
      detailUrl: buildDboRoute(DboRoute.DB_CONNECTIONS, detailBase),
    };

    return {
      id: id!,
      name,
      isHighestImpact: false,
      performanceAdvisor,
      caching,
      connections,
    };
  };

export const isInstanceOptimized = (
  features?: DboDatabaseInstanceV2['features']
): boolean => {
  return !!features?.advisor || !!features?.caching || !!features?.pooling;
};

export const toInstanceModelV2 = (
  instance: DboDatabaseInstanceV2
): InstanceViewModelV2 => {
  const {
    id,
    name,
    protocol,
    dbProxyId,
    cacheGroupId,
    endpoints,
    agentStatus,
    agentChartVersions,
    agentDeploymentStatus,
    proxyStatus,
    proxyChartVersions,
    proxyDeploymentStatus,
    cachingSummary,
    cloudDetails,
    connectionsSummary,
    features,
    recommendationsSummary,
    databases,
  } = instance;

  return {
    id: id!,
    isDemo: DEMO_ENTITY_IDS.has(id!),
    name,
    protocol,
    protocolLabel: dboProtocolToLabel[protocol],
    dbProxyId,
    cacheGroupId,
    endpoints,
    agentChartVersions,
    agentDeploymentStatus,
    proxyChartVersions,
    proxyDeploymentStatus,
    maxConnections: connectionsSummary?.maxConnections,
    dbEngine: cloudDetails?.dbEngine,
    cloudAccountId: cloudDetails?.cloudAccountId,
    cpuUsagePercentage: cloudDetails?.cpuUsagePercentage,
    memoryUsagePercentage: cloudDetails?.memoryUsagePercentage,
    maxConnectionsLastHourCount: cloudDetails?.maxConnectionsLastHourCount,
    averageConnectionsCount: cloudDetails?.averageConnectionsCount,
    queriesLastHourCount: cloudDetails?.queriesLastHourCount,
    provider: cloudDetails ? cloudDetails.provider : 'MANUAL',
    instanceClass: cloudDetails?.instanceClass,
    region: cloudDetails?.region,
    hitRate: cachingSummary?.hitRate,
    projectedHitRate: cachingSummary?.projectedHitRate,
    dbTimeSavePercentage: cachingSummary?.dbTimeSavePercentage,
    projectedDbTimeSavePercentage:
      cachingSummary?.projectedDbTimeSavePercentage,
    cacheHits: cachingSummary?.cacheHits,
    projectedCacheHits: cachingSummary?.projectedCacheHits,
    dqlQueries: cachingSummary?.dqlQueries,
    totalTimeMs: cachingSummary?.totalTimeMs,
    reduction: connectionsSummary?.reduction,
    incoming: connectionsSummary?.incoming,
    outgoing: connectionsSummary?.outgoing,
    recommendationsTotal: recommendationsSummary?.recommendationsTotal ?? 0,
    affectedQueriesCount: recommendationsSummary?.affectedQueriesCount ?? 0,
    affectedQueriesDbTimeMs:
      recommendationsSummary?.affectedQueriesDbTimeMs ?? 0,
    totalDbTimeMs: recommendationsSummary?.totalDbTimeMs,
    affectedQueriesDbTimePercentage:
      recommendationsSummary?.affectedQueriesDbTimePercentage,
    isOptimized: isInstanceOptimized(features),
    agentStatus: agentStatus ?? DatabaseInstanceAgentV2Enum.STATUS_UNSPECIFIED,
    proxyStatus: proxyStatus ?? DatabaseInstanceProxyV2Enum.STATUS_UNSPECIFIED,
    features: features ?? {
      advisor: false,
      caching: false,
      pooling: false,
    },

    databases: databases?.map(mapDatabaseToDashboardModel(id!, features)) || [],
  };
};

export const mapDboV2Instances = (
  res: DboV2DatabaseInstancesResponse
): DboV2InstancesOverviewModel =>
  res.items.map(toInstanceModelV2).reduce<DboV2InstancesOverviewModel>(
    (acc, row) => {
      acc.noInstances = false;
      if (row.isOptimized) {
        acc.optimized.push(row);
      } else {
        acc.other.push(row);
      }
      return acc;
    },
    { optimized: [], other: [], noInstances: true, total: res.items?.length }
  );
